import React from "react";
import { fireEvent, render, screen, within } from "@testing-library/react";
import "@testing-library/jest-dom";
import { MemoryRouter } from "react-router-dom";
import CanvasBrowser from "./CanvasBrowser";

let mockRole: "owner" | "admin" | "editor" | "user" = "user";
const mockUseCanvases = jest.fn();

jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({
    workspace: { id: "workspace-1" },
    isEditor: mockRole !== "user",
  }),
}));

jest.mock("../hooks", () => ({
  useCanvases: (...args: unknown[]) => mockUseCanvases(...args),
  useCanvasMutations: () => ({ deleteCanvas: jest.fn(), loading: false }),
}));

jest.mock("../components/MyVaultAgent", () => ({
  FloatingChatWidget: () => null,
}));

const canvas = {
  id: "canvas-1",
  name: "Employee app",
  description: "Example",
  canvas_type: "entry",
  status: "published",
  created_at: "2026-09-01T00:00:00Z",
};

describe("CanvasBrowser workspace roles", () => {
  beforeEach(() => {
    mockUseCanvases.mockReturnValue({
      canvases: [canvas],
      loading: false,
      error: null,
      refresh: jest.fn(),
    });
  });

  it("does not show the empty state while loading", () => {
    mockUseCanvases.mockReturnValue({
      canvases: [],
      loading: true,
      error: null,
      refresh: jest.fn(),
    });

    render(
      <MemoryRouter>
        <CanvasBrowser />
      </MemoryRouter>,
    );

    expect(
      screen.queryByText("No canvas apps created yet."),
    ).not.toBeInTheDocument();
  });

  it("shows the error and retry without claiming the list is empty", () => {
    const refresh = jest.fn();
    mockUseCanvases.mockReturnValue({
      canvases: [],
      loading: false,
      error: "Request timed out after 60s",
      refresh,
    });

    render(
      <MemoryRouter>
        <CanvasBrowser />
      </MemoryRouter>,
    );

    expect(screen.getByText("Request timed out after 60s")).toBeInTheDocument();
    expect(
      screen.queryByText("No canvas apps created yet."),
    ).not.toBeInTheDocument();
    fireEvent.click(screen.getByRole("button", { name: "Retry" }));
    expect(refresh).toHaveBeenCalledTimes(1);
  });

  it("shows the empty state after a successful empty response", () => {
    mockRole = "owner";
    mockUseCanvases.mockReturnValue({
      canvases: [],
      loading: false,
      error: null,
      refresh: jest.fn(),
    });

    render(
      <MemoryRouter>
        <CanvasBrowser />
      </MemoryRouter>,
    );

    expect(screen.getByText("No canvas apps created yet.")).toBeInTheDocument();
  });

  it("shows the list after a retry succeeds", () => {
    const refresh = jest.fn();
    mockUseCanvases.mockReturnValue({
      canvases: [],
      loading: false,
      error: "Request timed out after 60s",
      refresh,
    });
    const { rerender } = render(
      <MemoryRouter>
        <CanvasBrowser />
      </MemoryRouter>,
    );

    fireEvent.click(screen.getByRole("button", { name: "Retry" }));
    mockUseCanvases.mockReturnValue({
      canvases: [canvas],
      loading: false,
      error: null,
      refresh,
    });
    rerender(
      <MemoryRouter>
        <CanvasBrowser />
      </MemoryRouter>,
    );

    expect(screen.getByText("Employee app")).toBeInTheDocument();
    expect(
      screen.queryByText("No canvas apps created yet."),
    ).not.toBeInTheDocument();
  });

  it.each([
    ["owner", true],
    ["admin", true],
    ["editor", true],
    ["user", false],
  ] as const)(
    "shows app authoring controls only to %s when allowed",
    (role, canBuild) => {
      mockRole = role;
      render(
        <MemoryRouter>
          <CanvasBrowser />
        </MemoryRouter>,
      );

      expect(screen.getByText("Employee app")).toBeInTheDocument();
      expect(
        Boolean(screen.queryByRole("button", { name: "New Canvas" })),
      ).toBe(canBuild);
      expect(
        Boolean(screen.queryByRole("button", { name: "Edit canvas" })),
      ).toBe(canBuild);
      expect(
        Boolean(screen.queryByRole("button", { name: "Delete canvas" })),
      ).toBe(canBuild);
      expect(mockUseCanvases).toHaveBeenCalledWith("workspace-1", {
        includeUnpublished: canBuild,
      });
    },
  );

  it("renders a full-width title, reserves an empty description line, and omits an empty status chip", () => {
    mockRole = "owner";
    mockUseCanvases.mockReturnValue({
      canvases: [
        {
          ...canvas,
          name: "A very long application title that should stay on one line",
          description: "",
          status: "",
        },
      ],
      loading: false,
      error: null,
      refresh: jest.fn(),
    });

    render(
      <MemoryRouter>
        <CanvasBrowser />
      </MemoryRouter>,
    );

    const title = screen.getByText(
      "A very long application title that should stay on one line",
    );
    expect(title).toHaveStyle({
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    });
    expect(screen.queryByText("No description")).not.toBeInTheDocument();
    expect(screen.getByTestId("canvas-description-canvas-1")).toHaveStyle({
      minHeight: "20px",
    });
    expect(screen.queryByText("published")).not.toBeInTheDocument();
  });

  it("places Open, Edit, and Delete in the card footer", () => {
    mockRole = "owner";
    render(
      <MemoryRouter>
        <CanvasBrowser />
      </MemoryRouter>,
    );

    const card = screen.getByTestId("canvas-card-canvas-1");
    expect(card).toHaveClass("myvault-app-card");
    expect(card).toHaveStyle({ height: "264px" });
    const footer = screen.getByTestId("canvas-card-footer-canvas-1");
    expect(footer).toHaveClass("myvault-card-actions");
    const footerButtons = within(footer);
    expect(
      footerButtons.getByRole("button", { name: "Open canvas" }),
    ).toBeInTheDocument();
    expect(footer).toContainElement(
      footerButtons.getByRole("button", { name: "Edit canvas" }),
    );
    expect(footer).toContainElement(
      footerButtons.getByRole("button", { name: "Delete canvas" }),
    );
    expect(card).toContainElement(footer);
    expect(footer).toHaveStyle({ marginTop: "auto" });
    expect(footer).toHaveStyle({ flexWrap: "nowrap" });
  });

  it("keeps Delete behind the existing confirmation dialog", () => {
    mockRole = "owner";
    render(
      <MemoryRouter>
        <CanvasBrowser />
      </MemoryRouter>,
    );

    fireEvent.click(screen.getByRole("button", { name: "Delete canvas" }));
    expect(
      screen.getByRole("heading", { name: "Delete Canvas" }),
    ).toBeInTheDocument();
    expect(
      screen.getByText(/Are you sure you want to delete the canvas/),
    ).toBeInTheDocument();
  });
});
